import autoTable from 'jspdf-autotable';
import colors from '@packages/core/styles/colors';
import jsPDF from 'jspdf';
import SubaruLogo from '@packages/ui/assets/images/white-subaru-logo.png';
import TechShareLogo from '@packages/ui/assets/images/techshare-logo-white.png';
import { registerRobotoMediumBoldFont } from '@web/cvt/components/cvt-pdf/pdf-fonts/roboto-medium-bold';
import { registerRobotoRegularNormalFont } from '@web/cvt/components/cvt-pdf/pdf-fonts/roboto-regular-normal';
import { registerRobotoBoldBoldFont } from '@web/cvt/components/cvt-pdf/pdf-fonts/roboto-bold-bold';
import { CidCvnCheckResult } from '@packages/models/api/cid-cvn';

const variantBgColorMap: Record<CidCvnCheckResult, string> = {
    [CidCvnCheckResult.Pass]: colors.greenFour ?? '#daede8',
    [CidCvnCheckResult.Fail]: colors.redFour ?? '#fbdfdf',
    [CidCvnCheckResult.NoMatchFound]: colors.yellowFour ?? '#fff6e0',
};

export type CidCvnPdfOptions = {
    canViewDiagnosticInfo: boolean;
    result: CidCvnCheckResult;
    cid: string;
    cvn: string;
    fileName: string;
    metaDetails: { label: string; value: string }[];
    resultMessage: string;
    resultMessageReasons: string[];
    title: string;
};

export function generateCidCvnPdf({
    canViewDiagnosticInfo,
    result,
    cid,
    cvn,
    fileName,
    metaDetails,
    resultMessage,
    resultMessageReasons,
    title,
}: CidCvnPdfOptions) {
    const pdf = new jsPDF('p', 'pt', 'letter');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const headerH = 60;
    const leftPad = 32;
    const rightPad = 32;

    registerRobotoRegularNormalFont(pdf);
    registerRobotoMediumBoldFont(pdf);
    registerRobotoBoldBoldFont(pdf);

    const defaultFontName = 'Roboto';
    const defaultFontStyle = 'normal';

    pdf.setFont(defaultFontName, defaultFontStyle);

    pdf.setFillColor(colors.blueOne ?? '#1f518f');
    pdf.rect(0, 0, pageWidth, headerH, 'F');

    const techShareImgProps = pdf.getImageProperties(TechShareLogo);
    const techShareAspectRatio = techShareImgProps.width / techShareImgProps.height;
    const techShareHeight = 14;
    const techShareWidth = techShareHeight * techShareAspectRatio;
    pdf.addImage(
        TechShareLogo,
        'PNG',
        leftPad,
        headerH / 2 - techShareHeight / 2,
        techShareWidth,
        techShareHeight,
        undefined,
        'FAST'
    );

    const subaruImgProps = pdf.getImageProperties(SubaruLogo);
    const subaruAspectRatio = subaruImgProps.width / subaruImgProps.height;
    const subaruHeight = 24;
    const subaruWidth = subaruHeight * subaruAspectRatio;
    pdf.addImage(
        SubaruLogo,
        'PNG',
        pageWidth - rightPad - subaruWidth,
        headerH / 2 - subaruHeight / 2,
        subaruWidth,
        subaruHeight,
        undefined,
        'FAST'
    );

    let currentY = headerH + 36;

    if (title) {
        pdf.setFontSize(16);
        pdf.setFont(defaultFontName, 'bold');
        pdf.text(title, leftPad, currentY);
        pdf.setFont(defaultFontName, defaultFontStyle);
        pdf.setFontSize(11);
        currentY += 28;
    }

    if (metaDetails && metaDetails.length) {
        const labelFont = { size: 12, style: 'bold' as const };
        const valueFont = { size: 11, style: 'normal' as const };
        const col1X = leftPad;
        const col2X = leftPad + 240;
        let metaY = currentY;

        for (let i = 0; i < metaDetails.length; i += 2) {
            pdf.setFont(defaultFontName, labelFont.style);
            pdf.setFontSize(labelFont.size);
            pdf.text(metaDetails[i].label, col1X, metaY);
            pdf.setFont(defaultFontName, valueFont.style);
            pdf.setFontSize(valueFont.size);
            pdf.text(metaDetails[i].value, col1X, metaY + 15);

            if (metaDetails[i + 1]) {
                pdf.setFont(defaultFontName, labelFont.style);
                pdf.setFontSize(labelFont.size);
                pdf.text(metaDetails[i + 1].label, col2X, metaY);
                pdf.setFont(defaultFontName, valueFont.style);
                pdf.setFontSize(valueFont.size);
                pdf.text(metaDetails[i + 1].value, col2X, metaY + 15);
            }

            metaY += 50;
        }

        currentY = metaY + 4;
    }

    if (result) {
        const labelFont = { size: 12, style: 'bold' as const };
        pdf.setFontSize(labelFont.size);
        pdf.setFont(defaultFontName, labelFont.style);
        pdf.text('Result:', leftPad, currentY);

        const checkResultText = result.trim().toUpperCase().replace(/_/g, ' ');
        const resultX = leftPad + pdf.getTextWidth('Result: ') + 8;
        const resultY = currentY - 12;
        const paddingX = 8;
        const rectHeight = 16;

        pdf.setFontSize(10);
        pdf.setFont('Roboto-Bold', 'bold');

        const textWidth = pdf.getTextWidth(checkResultText);
        const rectWidth = textWidth + paddingX * 2;

        pdf.setFillColor(variantBgColorMap[result]);
        pdf.roundedRect(resultX, resultY, rectWidth, rectHeight, 4, 4, 'F');
        const centerX = resultX + rectWidth / 2;
        const textYOffset = rectHeight / 2 + 3.5;
        pdf.text(checkResultText, centerX, resultY + textYOffset, { align: 'center' });

        currentY += 35;
    }

    if (resultMessage) {
        pdf.setFontSize(12);
        pdf.setFont(defaultFontName, defaultFontStyle);

        const wrappedResultMessage = pdf.splitTextToSize(resultMessage, pageWidth - leftPad - rightPad);
        pdf.text(wrappedResultMessage, leftPad, currentY, { lineHeightFactor: 1.5 });
        currentY += 16 * wrappedResultMessage.length + 9;

        if (resultMessageReasons && resultMessageReasons.length) {
            pdf.setFontSize(12);
            pdf.setFont(defaultFontName, defaultFontStyle);
            resultMessageReasons.forEach((reason, index) => {
                const wrappedReason = pdf.splitTextToSize(
                    `${index + 1}. ${reason}`,
                    pageWidth - leftPad - rightPad - 10
                );
                pdf.text(wrappedReason, leftPad + 10, currentY);
                currentY += 16 * wrappedReason.length;
            });
            currentY += 4;
        }
    }

    if (canViewDiagnosticInfo) {
        const tableWidth = pageWidth - leftPad - rightPad;
        const colWidth = tableWidth / 2;

        autoTable(pdf, {
            startY: currentY,
            head: [['Calibration ID (CID)', 'Calibration Verification Number (CVN)']],
            body: [[cid, cvn]],
            margin: { left: leftPad, right: rightPad },
            theme: 'grid',
            tableWidth: 'auto',
            columnStyles: {
                0: { cellWidth: colWidth, halign: 'left' },
                1: { cellWidth: colWidth, halign: 'left' },
            },
            styles: {
                cellPadding: 16,
                font: defaultFontName,
                fontSize: 11,
                halign: 'left',
                lineWidth: 0.5,
                textColor: colors.black ?? '#071424',
                valign: 'middle',
            },
            headStyles: {
                cellPadding: 16,
                fillColor: colors.grayTwo ?? '#f3f3f3',
                font: defaultFontName,
                fontSize: 11,
                fontStyle: 'bold',
                halign: 'left',
                lineWidth: 0.5,
                textColor: colors.black ?? '#071424',
            },
            bodyStyles: {
                cellPadding: 16,
                font: defaultFontName,
                fontSize: 11,
                halign: 'left',
                lineWidth: 0.5,
                textColor: colors.black ?? '#071424',
            },
        });
    }

    pdf.save(fileName);
}
